import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { MessageSquare, BarChart3, Settings, User, Sun, Leaf } from 'lucide-react';
import { Reveal } from '../ui-custom/Reveal';
import { theme } from '../../utils/theme';

const iconMap = {
  MessageSquare: MessageSquare,
  BarChart3: BarChart3,
  Settings: Settings
};

const AppFeature = ({ icon, title, desc }) => {
  const IconComponent = iconMap[icon];
  
  return (
    <div className="flex items-start gap-5 p-4 rounded-xl hover:bg-white/5 transition-colors duration-300 cursor-default">
      <div 
        className="p-3 rounded-xl shrink-0 shadow-inner" 
        style={{ backgroundColor: 'rgba(255,255,255,0.03)', color: theme.colors.primary }}
      >
        <IconComponent size={24} />
      </div>
      <div>
        <h4 className="text-white font-bold text-xl mb-1">{title}</h4>
        <p className="text-m text-gray-400">{desc}</p>
      </div>
    </div>
  );
};

export const AppSection = ({ systemActive }) => {
  const { t } = useLanguage();
  
  const features = [
    { icon: 'Settings', title: t.app.feature1Title, desc: t.app.feature1Desc },
    { icon: 'BarChart3', title: t.app.feature2Title, desc: t.app.feature2Desc },
    { icon: 'MessageSquare', title: t.app.feature3Title, desc: t.app.feature3Desc }
  ];
  
  return (
    <section id="app" className="py-32 relative overflow-hidden border-t" style={{ borderColor: theme.colors.border}}>
      {/* Background blobs */}
      <div className="absolute top-1/4 left-0 w-[500px] h-[500px] bg-teal-900/20 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-orange-900/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="lg:grid lg:grid-cols-12 gap-16 items-center">
          
          <div className="lg:col-span-5 order-2 lg:order-1">
            <Reveal>
              <h2 className="text-3xl md:text-5xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-b from-white to-gray-400">
                {t.app.title}
              </h2>
              <p 
                className="mb-12 text-lg" 
                style={{ color: theme.colors.textMuted }}
              >
                {t.app.description}
              </p>
            </Reveal>

            <div className="space-y-8">
              {features.map((feat, i) => (
                <Reveal key={i} delay={i * 150}>
                  <AppFeature {...feat} />
                </Reveal>
              ))}
            </div>
          </div>

          {/* Mobile Mockup with Animations */}
          <Reveal 
            className="lg:col-span-7 order-1 lg:order-2 flex justify-center lg:justify-end relative" 
            delay={200}
          >
            <div className="animate-float relative w-[320px] h-[650px] bg-black rounded-[3rem] border-[6px] border-[#222] shadow-2xl overflow-hidden z-10">
              {/* Screen Content */}
              <div className="h-full w-full bg-[#0a0a0a] overflow-hidden relative">
                {/* Top Bar */}
                <div className="pt-12 px-6 pb-4 flex justify-between items-center z-20 relative">
                  <span className="text-white font-bold text-xl">Dashboard</span>
                  <div className="w-8 h-8 rounded-full bg-gray-800 border border-gray-700 flex items-center justify-center">
                    <User size={14} />
                  </div>
                </div>

                {/* Savings Card */}
                <div 
                  className="mx-4 p-6 rounded-3xl mb-6 relative overflow-hidden group transition-transform hover:scale-[1.02]" 
                  style={{ background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.accent})` }}
                >
                  <div className="relative z-10">
                    <div className="text-black/60 font-bold uppercase tracking-wider text-xs mb-1">
                      Économies (Juin)
                    </div>
                    <div className="text-4xl font-black text-black mb-2 flex items-baseline gap-1">
                      42.50 <span className="text-lg font-bold opacity-60">$</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs font-bold text-black/70 bg-black/10 px-3 py-1.5 rounded-full w-fit">
                      <Leaf size={12} fill="currentColor" /> -15kg CO₂
                    </div>
                  </div>
                  {/* Decorative circles */}
                  <div className="absolute -right-4 -top-4 w-24 h-24 bg-white/20 rounded-full blur-xl" />
                </div>

                {/* Real-time Widget */}
                <div className="mx-4 mb-6">
                  <div className="text-xs font-bold text-gray-500 mb-3 uppercase tracking-wider">
                    Temps Réel
                  </div>
                  <div className="bg-[#151515] p-4 rounded-2xl border border-white/5 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors duration-500 ${
                        systemActive ? 'bg-orange-500/20 text-orange-500' : 'bg-gray-800 text-gray-500'
                      }`}>
                        <Sun 
                          size={24} 
                          className={systemActive ? 'animate-spin-slow' : ''} 
                          style={{ animationDuration: '3s' }}
                        />
                      </div>
                      <div>
                        <div className="text-white font-bold">Salon Sud</div>
                        <div className="text-xs text-gray-400">
                          {systemActive ? 'Blocage actif' : 'Passif'}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-white">22°</div>
                      <div className="text-xs text-gray-500">Intérieur</div>
                    </div>
                  </div>
                </div>

                {/* Animated Chart */}
                <div className="mx-4 bg-[#151515] p-5 rounded-2xl border border-white/5 h-48 flex flex-col justify-between">
                  <div className="flex justify-between items-center text-xs text-gray-500 font-bold">
                    <span>PERFORMANCE</span>
                    <span className="text-green-500">+12% vs Hier</span>
                  </div>
                  <div className="flex items-end justify-between gap-2 h-24">
                    {[35, 55, 45, 70, 60, 85, 50].map((h, i) => (
                      <div key={i} className="w-full bg-gray-800 rounded-t-sm relative overflow-hidden group">
                        <div 
                          className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-teal-800 to-teal-400 transition-all duration-1000 ease-out" 
                          style={{ 
                            height: systemActive ? `${h}%` : '0%', 
                            transitionDelay: `${i * 100}ms` 
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
